import {
  hasPodcastAudioFeed,
  isTranscriptMediaSourceId,
} from "./connectors/transcript-sources";
import type {
  ConnectorCategory,
  ConnectorId,
  ConnectorSnapshot,
  ConnectorStatus,
  MediaItemType,
} from "./connectors/types";
import type { TimelineEvidenceEvent } from "./timeline-types";
import { systems, type SystemId } from "./systems";
import {
  buildSourceAuthorityGraph,
  emptySourceAuthorityGraph,
  type SourceAuthorityGraph,
} from "./source-authority";

export type SignalSourceAdapter =
  | "registry"
  | "market_snapshot"
  | "media_feed"
  | "transcript_pipeline"
  | "audio_transcription"
  | "incentive_profile"
  | "official_sources";

export type SignalSourceNode = {
  id: ConnectorId;
  name: string;
  category: ConnectorCategory;
  kind: ConnectorSnapshot["kind"];
  status: ConnectorStatus;
  ingestionState: "observed" | "registered" | "credentials_required" | "unavailable";
  adapters: SignalSourceAdapter[];
  observedAt: string;
  headline: string;
  url: string;
  systemIds: SystemId[];
};

export type SignalSourceObservation = {
  id: string;
  sourceId: ConnectorId;
  kind: "market_snapshot" | "media_item" | "institution_snapshot";
  observedAt: string;
  title: string;
  detail: string;
  url?: string;
  systemIds: SystemId[];
  externalId?: string;
  mediaType?: MediaItemType;
  durationSeconds?: number;
};

export type SignalSourceRegistry = {
  schemaVersion: 1;
  refreshedAt: string;
  sources: SignalSourceNode[];
  observations: SignalSourceObservation[];
  sourceAuthority?: SourceAuthorityGraph;
};

export const emptySignalSourceRegistry: SignalSourceRegistry = {
  schemaVersion: 1,
  refreshedAt: "",
  sources: [],
  observations: [],
  sourceAuthority: emptySourceAuthorityGraph,
};

function adapters(connector: ConnectorSnapshot): SignalSourceAdapter[] {
  const values: SignalSourceAdapter[] = ["registry"];
  if (connector.category === "market") values.push("market_snapshot");
  if (connector.category === "media" && connector.media) values.push("media_feed");
  if (isTranscriptMediaSourceId(connector.id)) {
    values.push("transcript_pipeline");
    if (hasPodcastAudioFeed(connector.id)) values.push("audio_transcription");
  }
  if (connector.category === "person") values.push("incentive_profile");
  if (connector.category === "institution") values.push("official_sources");
  return values;
}

function ingestionState(status: ConnectorStatus): SignalSourceNode["ingestionState"] {
  if (status === "live") return "observed";
  return status;
}

function sourceSystemIndex() {
  const index = new Map<ConnectorId, SystemId[]>();
  systems.forEach((system) => {
    system.sourceLinks.forEach((source) => {
      const linked = index.get(source.id) ?? [];
      if (!linked.includes(system.id)) linked.push(system.id);
      index.set(source.id, linked);
    });
  });
  return index;
}

function observationId(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9_-]+/g, "-").replace(/^-|-$/g, "");
}

function observationHour(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return observationId(value);
  parsed.setUTCMinutes(0, 0, 0);
  return observationId(parsed.toISOString());
}

function normalizedTitle(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function youtubeVideoId(url?: string) {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?[^#]*\bv=|shorts\/|live\/))([a-zA-Z0-9_-]{6,})/i);
  return match?.[1] ?? null;
}

const mediaKindLabels: Record<MediaItemType, string> = {
  podcast: "Full episode",
  short: "Short",
  other: "Other video",
  article: "Latest article",
};

function mediaObservationIdentity(observation: SignalSourceObservation) {
  const externalId = observation.externalId ?? youtubeVideoId(observation.url);
  if (externalId) return `video:${externalId}`;
  return `media:${normalizedTitle(observation.title)}:${observation.observedAt.slice(0, 10)}`;
}

export function signalObservationTimelineEvents(
  observations: SignalSourceObservation[],
): TimelineEvidenceEvent[] {
  const events = new Map<string, TimelineEvidenceEvent>();
  observations
    .filter((observation) => observation.kind === "media_item")
    .forEach((observation) => {
      const id = mediaObservationIdentity(observation);
      const existing = events.get(id);
      const mediaType = observation.mediaType
        ?? (observation.detail in mediaKindLabels
          ? observation.detail as MediaItemType
          : "other");
      const nextDetail = observation.durationSeconds === undefined
        ? "Published source item"
        : `${Math.max(1, Math.round(observation.durationSeconds / 60))} minute runtime`;
      if (existing) {
        if (!existing.sourceIds.includes(observation.sourceId)) {
          existing.sourceIds.push(observation.sourceId);
        }
        if (observation.durationSeconds !== undefined) {
          existing.kind = mediaKindLabels[mediaType];
          existing.detail = nextDetail;
        } else if (existing.kind === "Other video" && mediaType !== "other") {
          existing.kind = mediaKindLabels[mediaType];
        }
        return;
      }
      events.set(id, {
        id,
        at: observation.observedAt,
        kind: mediaKindLabels[mediaType],
        sourceIds: [observation.sourceId],
        title: observation.title,
        detail: nextDetail,
        url: observation.url,
      });
    });
  return Array.from(events.values()).sort((left, right) =>
    Date.parse(right.at) - Date.parse(left.at));
}

export function buildSignalSourceRegistry(
  connectors: ConnectorSnapshot[],
  refreshedAt = new Date().toISOString(),
): SignalSourceRegistry {
  const systemIndex = sourceSystemIndex();
  const sources = connectors.map<SignalSourceNode>((connector) => ({
    id: connector.id,
    name: connector.name,
    category: connector.category,
    kind: connector.kind,
    status: connector.status,
    ingestionState: ingestionState(connector.status),
    adapters: adapters(connector),
    observedAt: connector.observedAt,
    headline: connector.headline,
    url: connector.docsUrl,
    systemIds: [...(systemIndex.get(connector.id) ?? [])],
  }));

  const observations = connectors.flatMap<SignalSourceObservation>((connector) => {
    const systemIds = [...(systemIndex.get(connector.id) ?? [])];
    if (connector.category === "market" && connector.status === "live") {
      return [{
        // One mutable point per market and UTC hour preserves a useful sequence
        // without turning every page refresh into another historical row.
        id: `observation:${connector.id}:${observationHour(connector.observedAt)}`,
        sourceId: connector.id,
        kind: "market_snapshot",
        observedAt: connector.observedAt,
        title: connector.headline,
        detail: `${connector.instrument} · ${connector.primary.label} ${connector.primary.value} · ${connector.secondary.label} ${connector.secondary.value}`,
        url: connector.docsUrl,
        systemIds,
      }];
    }
    if (connector.category === "institution") {
      const officialTypes = Array.from(new Set(
        connector.institution?.officialSources.map((source) => source.type) ?? [],
      ));
      return [{
        // This is a coverage register, not a dated filing or release. Reusing
        // the id keeps the register current instead of fabricating history.
        id: `observation:${connector.id}:registry`,
        sourceId: connector.id,
        kind: "institution_snapshot",
        observedAt: connector.observedAt,
        title: `${connector.name} evidence register`,
        detail: officialTypes.length
          ? `${officialTypes.length} official surface types registered: ${officialTypes.join(", ")}`
          : "Official institutional surface registered; live updates are not connected.",
        url: connector.docsUrl,
        systemIds,
      }];
    }
    if (connector.category !== "media" || !connector.media) return [];
    return connector.media.items.map((item) => ({
      id: `observation:${connector.id}:${observationId(item.videoId ?? `${item.title}-${item.publishedAt}`)}`,
      sourceId: connector.id,
      kind: "media_item" as const,
      observedAt: item.publishedAt,
      title: item.title,
      detail: item.type,
      url: item.url,
      systemIds,
      externalId: item.videoId,
      mediaType: item.type,
      durationSeconds: item.durationSeconds,
    }));
  });

  return {
    schemaVersion: 1,
    refreshedAt,
    sources,
    observations,
    sourceAuthority: buildSourceAuthorityGraph(connectors),
  };
}
